import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

export interface ReviewNodeMeta {
  outputKey: string;
  schema?: Record<string, unknown>;
  isAudioMode: boolean;
}

/**
 * Per-run_classifier-node metadata for autocomplete. `taxonomy` is the
 * classifier_config's JSON schema (same shape as review's response_fields_schema).
 * Each boolean property becomes a suggestion under `<outputKey>.labels.<key>`,
 * non-boolean under `<outputKey>.data.<key>` — mirrors the BaseAgent.splitByType
 * partition the executor performs at runtime.
 */
export interface ClassifierNodeMeta {
  outputKey: string;
  taxonomy?: Record<string, unknown>;
}

/** Per-run_quality-node metadata. Same shape as ClassifierNodeMeta but
 * the bool partition lives under `flags` and the agent also produces a
 * top-level `reasoning` string. */
export interface QualityNodeMeta {
  outputKey: string;
  rubric?: Record<string, unknown>;
}

/**
 * Per-agent-node metadata. Unlike classifier/quality, an agent node's output is
 * spread at the top level of its context entry — the schema the author drew in
 * the UI is exactly the shape they template against — so properties suggest as
 * `<outputKey>.<field>` with no labels/data wrapper. The split/persistence
 * details live under `<outputKey>._meta`.
 */
export interface AgentNodeMeta {
  outputKey: string;
  outputSchema?: Record<string, unknown>;
}

export interface AnalyticsNodeMeta {
  outputKey: string;
  metricKeys: string[];
}

export interface WebhookNodeMeta {
  testPayload?: Record<string, unknown>;
}

interface VariablePickerProps {
  onSelect: (variable: string) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewOutputKeys?: string[];
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  webhookNodeMeta?: WebhookNodeMeta;
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

interface VariableGroup {
  namespace: string;
  variables: { path: string; label: string }[];
}

const COMMON_GROUPS: VariableGroup[] = [
  {
    namespace: 'contact',
    variables: [
      { path: 'contact.name', label: 'name' },
      { path: 'contact.phone', label: 'phone' },
    ],
  },
  {
    namespace: 'now (UTC, runtime)',
    variables: [
      { path: 'now', label: 'now (full ISO timestamp)' },
      { path: 'now_date', label: 'now_date (YYYY-MM-DD)' },
      { path: 'now_time', label: 'now_time (HH:MM)' },
      { path: 'now_hour', label: 'now_hour (0-23)' },
      { path: 'now_day_of_week', label: 'now_day_of_week (0=Sun … 6=Sat)' },
      { path: 'current_month', label: 'current_month (1st of month, YYYY-MM-DD)' },
      { path: 'time_uk', label: 'time_uk (HH:MM, UK local — Europe/London)' },
    ],
  },
  {
    namespace: 'date_offset (ISO dates)',
    variables: [
      { path: 'date_offset:0', label: 'today (YYYY-MM-DD)' },
      { path: 'date_offset:-1', label: 'yesterday' },
      { path: 'date_offset:-7', label: '7 days ago' },
      { path: 'date_offset:-30', label: '30 days ago' },
      { path: 'date_offset:1', label: 'tomorrow' },
    ],
  },
  {
    namespace: 'sms (global, last sms node wins)',
    variables: [
      { path: 'sms_sent', label: 'sms_sent (true if last SMS succeeded)' },
      { path: 'sms_not_sent', label: 'sms_not_sent (true if last SMS failed)' },
      { path: 'sms_sent_to', label: 'sms_sent_to (recipients)' },
    ],
  },
  {
    namespace: 'per-node output (use node ID)',
    variables: [
      { path: 'nodes.<nodeId>.status', label: 'status (success/error/skipped)' },
      { path: 'nodes.<nodeId>.sms_sent', label: 'sms_sent (per SMS node)' },
      { path: 'nodes.<nodeId>.sms_not_sent', label: 'sms_not_sent (per SMS node)' },
      { path: 'nodes.<nodeId>.branch', label: 'branch (per condition node)' },
    ],
  },
];

const CALL_GROUPS: VariableGroup[] = [
  {
    namespace: 'call',
    variables: [
      { path: 'call.id', label: 'id' },
      { path: 'call.direction', label: 'direction' },
      { path: 'call.call_transport', label: 'call_transport' },
      { path: 'call.channel', label: 'channel' },
      { path: 'call.modality', label: 'modality' },
      { path: 'call.status', label: 'status' },
      { path: 'call.outcome', label: 'outcome' },
      { path: 'call.duration', label: 'duration' },
      { path: 'call.phone_number', label: 'phone_number' },
      { path: 'call.lead_data', label: 'lead_data' },
      { path: 'call.availability_data', label: 'availability_data' },
      { path: 'call.callback_data', label: 'callback_data' },
      { path: 'call.shift_data', label: 'shift_data' },
    ],
  },
  {
    namespace: 'campaign',
    variables: [
      { path: 'campaign.name', label: 'name' },
      { path: 'campaign.id', label: 'id' },
    ],
  },
  {
    namespace: 'config',
    variables: [
      { path: 'config.notification_emails', label: 'notification_emails' },
      { path: 'config.high_intent_emails', label: 'high_intent_emails' },
    ],
  },
  {
    namespace: 'enriched (scraped)',
    variables: [
      { path: 'review.enriched_vehicle.name', label: 'vehicle name' },
      { path: 'review.enriched_vehicle.registration', label: 'registration' },
      { path: 'review.enriched_vehicle.price', label: 'price' },
      { path: 'review.enriched_vehicle.mileage', label: 'mileage' },
      { path: 'review.enriched_vehicle.location', label: 'location' },
      { path: 'review.enriched_vehicle.checkout_url', label: 'checkout/reservation URL' },
    ],
  },
];

const DEALERKIT_GROUPS: VariableGroup[] = [
  {
    namespace: 'dealerkit (lead)',
    variables: [
      { path: 'dealerkit.id', label: 'lead ID' },
      { path: 'dealerkit.type', label: 'lead type (buying/sales)' },
      { path: 'dealerkit.status', label: 'status (active/closing/won/lost)' },
      { path: 'dealerkit.webhook_type', label: 'webhook type (lead.created/updated)' },
      { path: 'dealerkit.dealer_id', label: 'dealer ID' },
      { path: 'dealerkit.created_at', label: 'lead created at' },
      { path: 'dealerkit.updated_at', label: 'lead updated at' },
    ],
  },
  {
    namespace: 'dealerkit.person',
    variables: [
      { path: 'dealerkit.person.title', label: 'title (Mr/Mrs/etc)' },
      { path: 'dealerkit.person.first_name', label: 'first_name' },
      { path: 'dealerkit.person.last_name', label: 'last_name' },
      { path: 'dealerkit.person.email', label: 'email' },
      { path: 'dealerkit.person.telephone', label: 'telephone' },
      { path: 'dealerkit.person.company_name', label: 'company_name' },
    ],
  },
  {
    namespace: 'dealerkit.vehicle (first vehicle of interest)',
    variables: [
      { path: 'dealerkit.vehicle.registration', label: 'registration (VRM)' },
      { path: 'dealerkit.vehicle.make', label: 'make' },
      { path: 'dealerkit.vehicle.model', label: 'model' },
      { path: 'dealerkit.vehicle.derivative', label: 'derivative (full spec)' },
      { path: 'dealerkit.vehicle.colour', label: 'colour' },
      { path: 'dealerkit.vehicle.fuel_type', label: 'fuel_type' },
      { path: 'dealerkit.vehicle.transmission_type', label: 'transmission_type' },
      { path: 'dealerkit.vehicle.body_type', label: 'body_type' },
      { path: 'dealerkit.vehicle.mileage', label: 'mileage' },
      { path: 'dealerkit.vehicle.engine_size', label: 'engine_size' },
    ],
  },
  {
    namespace: 'dealerkit (context)',
    variables: [
      { path: 'dealerkit.owner.name', label: 'sales owner name' },
      { path: 'dealerkit.location.name', label: 'dealership location' },
      { path: 'dealerkit.location.telephone', label: 'dealership phone' },
      { path: 'dealerkit.location.email', label: 'dealership email' },
      { path: 'dealerkit.stage.name', label: 'lead stage' },
      { path: 'dealerkit.referral_source.name', label: 'referral source' },
      { path: 'dealerkit.last_activity.type', label: 'last activity type' },
      { path: 'dealerkit.last_activity.title', label: 'last activity title' },
      { path: 'dealerkit.last_activity.date', label: 'last activity date' },
    ],
  },
];

const WEBHOOK_GROUPS: VariableGroup[] = [
  {
    namespace: 'webhook',
    variables: [
      { path: 'webhook.method', label: 'method (POST)' },
      { path: 'webhook.ip', label: 'ip (sender IP)' },
      { path: 'webhook.received_at', label: 'received_at (ISO timestamp)' },
    ],
  },
];

function buildWebhookBodyGroup(testPayload?: Record<string, unknown>): VariableGroup {
  const base: { path: string; label: string }[] = [
    { path: 'webhook.body', label: 'body (full object)' },
  ];

  if (testPayload) {
    const schema = testPayload.schema as Record<string, unknown> | undefined;
    if (schema?.properties) {
      base.push(
        ...extractSchemaVariables(
          (schema as { properties: Record<string, unknown> }).properties,
          'webhook.body',
        ),
      );
    }
  }

  return { namespace: 'webhook.body (request payload)', variables: base };
}

function buildAnalyticsGroupFromMeta(meta: AnalyticsNodeMeta): VariableGroup {
  const { outputKey: key, metricKeys } = meta;
  const base: { path: string; label: string }[] = metricKeys.flatMap((mk) => [
    { path: `${key}.${mk}.count`, label: `${mk}.count` },
    { path: `${key}.${mk}.inbound`, label: `${mk}.inbound` },
    { path: `${key}.${mk}.outbound`, label: `${mk}.outbound` },
    { path: `${key}.${mk}.change`, label: `${mk}.change` },
  ]);
  return { namespace: `${key} (analytics)`, variables: base };
}

function getVariableGroups(triggerType: string): VariableGroup[] {
  switch (triggerType) {
    case 'dealerkit':
      return [...DEALERKIT_GROUPS, ...COMMON_GROUPS];
    case 'any':
      return [...CALL_GROUPS, ...DEALERKIT_GROUPS, ...COMMON_GROUPS];
    case 'scheduled':
      return [...COMMON_GROUPS];
    case 'webhook':
      return [...WEBHOOK_GROUPS, ...COMMON_GROUPS];
    // Event payloads are exposed at webhook.body (same shape as webhook
    // triggers) and call/contact context is hydrated when call_id is present.
    case 'event':
      return [...WEBHOOK_GROUPS, ...CALL_GROUPS, ...COMMON_GROUPS];
    case 'call':
    default:
      return [...CALL_GROUPS, ...COMMON_GROUPS];
  }
}

function extractSchemaVariables(
  properties: Record<string, unknown>,
  prefix: string,
): { path: string; label: string }[] {
  const result: { path: string; label: string }[] = [];
  for (const [key, propDef] of Object.entries(properties)) {
    const fullPath = `${prefix}.${key}`;
    result.push({ path: fullPath, label: key });
    const prop = propDef as Record<string, unknown> | undefined;
    if (!prop) continue;
    if (prop.type === 'object' && prop.properties) {
      result.push(...extractSchemaVariables(prop.properties as Record<string, unknown>, fullPath));
    }
    if (prop.type === 'array' && prop.items) {
      const items = prop.items as Record<string, unknown>;
      if (items.properties) {
        result.push(
          ...extractSchemaVariables(items.properties as Record<string, unknown>, fullPath),
        );
      }
    }
  }
  return result;
}

const STANDARD_REVIEW_FIELDS = new Set([
  'reviewed_outcome',
  'outcome_matches',
  'reviewed_lead_data',
  'lead_data',
  'lead_data_matches',
  'reviewed_availability',
  'availability_data',
  'availability_matches',
  'discrepancies',
  'analysis',
  'confidence_score',
  'generated_transcript',
]);

function buildReviewGroupFromMeta(meta: ReviewNodeMeta): VariableGroup {
  const { outputKey: key, schema, isAudioMode } = meta;
  const base: { path: string; label: string }[] = [
    { path: `${key}.reviewed_outcome`, label: 'reviewed_outcome' },
    { path: `${key}.ai_analysis`, label: 'ai_analysis' },
    { path: `${key}.confidence_score`, label: 'confidence_score' },
    { path: `${key}.additional_review_data`, label: 'additional_review_data' },
  ];

  if (isAudioMode) {
    base.push(
      { path: `${key}.generated_transcript`, label: 'generated_transcript' },
      {
        path: `${key}.generated_transcript.fullTranscript`,
        label: 'generated_transcript.fullTranscript',
      },
    );
  }

  if (schema && typeof schema === 'object') {
    const properties = (schema as { properties?: Record<string, unknown> }).properties ?? {};
    for (const [field, propDef] of Object.entries(properties)) {
      const vars = extractSchemaVariables({ [field]: propDef }, key);
      base.push(...vars);
      if (!STANDARD_REVIEW_FIELDS.has(field)) {
        base.push(...extractSchemaVariables({ [field]: propDef }, `${key}.additional_review_data`));
      }
    }
  }

  return { namespace: key, variables: base };
}

function buildReviewGroups(
  metas?: ReviewNodeMeta[],
  fallbackSchema?: Record<string, unknown>,
): VariableGroup[] {
  if (metas && metas.length > 0) {
    return metas.map((m) => buildReviewGroupFromMeta(m));
  }
  return [
    buildReviewGroupFromMeta({ outputKey: 'review', schema: fallbackSchema, isAudioMode: false }),
  ];
}

/**
 * Walk a flat JSON schema and partition properties by type. Booleans become
 * suggestions under `<prefix>.<bucket>` (labels/flags), everything else under
 * `<prefix>.data`. Mirrors how BaseAgent.splitByType partitions at runtime.
 */
function partitionByType(
  properties: Record<string, unknown>,
  prefix: string,
  boolBucket: 'labels' | 'flags',
): { path: string; label: string }[] {
  const out: { path: string; label: string }[] = [];
  for (const [key, propDef] of Object.entries(properties)) {
    const prop = propDef as Record<string, unknown> | undefined;
    const isBool = prop?.type === 'boolean';
    const bucket = isBool ? boolBucket : 'data';
    const fullPath = `${prefix}.${bucket}.${key}`;
    out.push({ path: fullPath, label: `${bucket}.${key}` });

    // Recurse into nested object/array structures only under the data bucket.
    if (!isBool && prop?.type === 'object' && prop.properties) {
      out.push(...extractSchemaVariables(prop.properties as Record<string, unknown>, fullPath));
    }
    if (!isBool && prop?.type === 'array' && prop.items) {
      const items = prop.items as Record<string, unknown>;
      if (items.properties) {
        out.push(...extractSchemaVariables(items.properties as Record<string, unknown>, fullPath));
      }
    }
  }
  return out;
}

function buildClassifierGroupFromMeta(meta: ClassifierNodeMeta): VariableGroup {
  const { outputKey: key, taxonomy } = meta;
  const base: { path: string; label: string }[] = [
    { path: `${key}.confidenceScore`, label: 'confidenceScore' },
    { path: `${key}.taxonomyVersion`, label: 'taxonomyVersion' },
    { path: `${key}.classifierConfigId`, label: 'classifierConfigId' },
  ];
  if (taxonomy && typeof taxonomy === 'object') {
    const properties = (taxonomy as { properties?: Record<string, unknown> }).properties ?? {};
    base.push(...partitionByType(properties, key, 'labels'));
  }
  return { namespace: `${key} (classifier)`, variables: base };
}

function buildQualityGroupFromMeta(meta: QualityNodeMeta): VariableGroup {
  const { outputKey: key, rubric } = meta;
  const base: { path: string; label: string }[] = [
    { path: `${key}.reasoning`, label: 'reasoning' },
    { path: `${key}.confidenceScore`, label: 'confidenceScore' },
    { path: `${key}.rubricVersion`, label: 'rubricVersion' },
    { path: `${key}.qualityConfigId`, label: 'qualityConfigId' },
  ];
  if (rubric && typeof rubric === 'object') {
    const properties = (rubric as { properties?: Record<string, unknown> }).properties ?? {};
    base.push(...partitionByType(properties, key, 'flags'));
  }
  return { namespace: `${key} (quality)`, variables: base };
}

function buildAgentGroupFromMeta(meta: AgentNodeMeta): VariableGroup {
  const { outputKey: key, outputSchema } = meta;
  const properties =
    outputSchema && typeof outputSchema === 'object'
      ? ((outputSchema as { properties?: Record<string, unknown> }).properties ?? {})
      : {};
  const variables = Object.keys(properties).map((name) => ({
    path: `${key}.${name}`,
    label: name,
  }));
  variables.push(
    { path: `${key}._meta.confidenceScore`, label: '_meta.confidenceScore' },
    { path: `${key}._meta.reasoning`, label: '_meta.reasoning' },
  );
  return { namespace: `${key} (agent)`, variables };
}

export const VariablePicker = ({
  onSelect,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  webhookNodeMeta,
  triggerType = 'call',
}: VariablePickerProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const reviewGroups = buildReviewGroups(reviewNodeMetas, responseFieldsSchema);
  const classifierGroups = (classifierNodeMetas ?? []).map(buildClassifierGroupFromMeta);
  const qualityGroups = (qualityNodeMetas ?? []).map(buildQualityGroupFromMeta);
  const agentGroupsFromMetas = (agentNodeMetas ?? []).map(buildAgentGroupFromMeta);
  const analyticsGroups = (analyticsNodeMetas ?? []).map(buildAnalyticsGroupFromMeta);
  const webhookBodyGroup =
    triggerType === 'webhook'
      ? buildWebhookBodyGroup(webhookNodeMeta?.testPayload ?? undefined)
      : null;
  const baseGroups = getVariableGroups(triggerType);
  const agentGroups =
    triggerType !== 'dealerkit'
      ? [
          ...reviewGroups,
          ...classifierGroups,
          ...qualityGroups,
          ...agentGroupsFromMetas,
          ...analyticsGroups,
        ]
      : [...agentGroupsFromMetas, ...analyticsGroups];
  const callGroup = baseGroups.find((g) => g.namespace === 'call');
  const restGroups = baseGroups.filter((g) => g.namespace !== 'call');
  const groups: VariableGroup[] = callGroup
    ? [callGroup, ...agentGroups, ...restGroups]
    : triggerType === 'webhook' && webhookBodyGroup
      ? [webhookBodyGroup, ...agentGroups, ...restGroups]
      : [...agentGroups, ...restGroups];

  const handleSelect = (path: string) => {
    onSelect(`{{${path}}}`);
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 px-2 font-mono text-xs"
        onClick={() => setOpen((v) => !v)}
        title="Insert variable"
      >
        {'{ }'}
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="border-border bg-background absolute top-full right-0 z-50 mt-1 max-h-72 w-56 overflow-y-auto rounded-md border shadow-lg">
            {groups.map((group) => (
              <div key={group.namespace}>
                <div className="bg-muted/50 text-muted-foreground px-3 py-1.5 text-[10px] font-semibold tracking-wider uppercase">
                  {group.namespace}
                </div>
                {group.variables.map((v) => (
                  <button
                    key={v.path}
                    type="button"
                    className="hover:bg-accent hover:text-accent-foreground w-full px-3 py-1.5 text-left font-mono text-xs"
                    onClick={() => handleSelect(v.path)}
                  >
                    {`{{${v.path}}}`}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
